import { getClickTracking } from 'shared/tracking/trackingUtils';
import { getAdContext } from 'shared/utils/adContext';
import { fireClickTracking, firePixelTracking, } from 'shared/utils/fireTracking';
import { validUrlOrUndefined } from 'shared/utils/urls/misc';
/**
 * Returns mouse event related props (onClick event and styles).
 *
 * @param destinationUrl Optional click-thru URL.
 * @param inEditor Only specifically set in the editor. `undefined` elsewhere.
 * @param hoverAnimation If a hover animation is provided, pointer events must be enabled.
 */
export const mouseEventProps = (symbol, inEditor, hoverAnimation) => {
    if (inEditor) {
        // Ensure Symbols can always be selected in editor.
        return { mouseEventStyles: { pointerEvents: 'auto' } };
    }
    const clickUrls = getClickUrls(symbol);
    if (clickUrls.destinationUrl || clickUrls.parallelUrls.length > 0) {
        return onClickProps(clickUrls);
    }
    return {
        mouseEventStyles: {
            // If there is no hoverAnimation, disabled pointer events
            // in order to allow Symbols with a lower z-index to receive
            // mouseEnter/mouseLeave events.
            pointerEvents: hoverAnimation ? 'auto' : 'none',
        },
    };
};
/**
 * Returns the click handler and styles necessary for a
 * Symbol to support click-to-url (with click tracking).
 */
const onClickProps = (clickUrls) => ({
    onClick: (e) => {
        // We use `preventDefault()` to signal to other Symbols up the
        // tree that we have already handled this event on some level.
        //
        // Example of where this is useful:
        //
        // 1. A CTA has a parallel click tracker but no destination `clickUrl`.
        // 2. The CTA's parallel tracker is fired but, but we allow the event
        //    to propagate up, so that an ancestor Symbol's `clickUrl` can be processed.
        // 3. Let's say eventually the event propagates up to the `stage`. It for sure
        //    has a `clickUrl` but probably a parallel tracker too. We don't want
        //    to fire that parallel tracker, since we already fired the CTA's.
        // 4. The `stage` will know not to fire its parallel tracker,
        //    because `e.defaultPrevented` will be `true`.
        const wasAlreadyProcessed = e.defaultPrevented;
        e.preventDefault();
        if (!wasAlreadyProcessed) {
            clickUrls.parallelUrls.forEach(u => firePixelTracking(u));
        }
        // If this Symbol has a `destinationUrl`, we direct the user to that destination
        // and stop the event from propagating up to ancestor Symbols. Otherwise, we allow
        // the event to propagate up until a Symbol with a `destinationUrl` is found.
        if (clickUrls.destinationUrl) {
            e.stopPropagation();
            fireClickTracking(clickUrls.destinationUrl);
        }
    },
    mouseEventStyles: {
        cursor: 'pointer',
        pointerEvents: 'auto',
    },
    clickClass: 'clickthru',
});
/**
 * Determine the destination and parallel URLs for a Symbol.
 */
export const getClickUrls = (symbol) => {
    var _a;
    // Click override is set with the `ct` query param.
    const externalOverride = symbol.type === 'stage' ? getExternalClickTag() : undefined;
    const clickTracking = getClickTracking(symbol.id);
    const parallelUrls = clickTracking
        .filter(t => t.isParallel)
        .map(t => t.url);
    // Click override always takes precedence for `stage`.
    if (externalOverride) {
        return {
            destinationUrl: externalOverride,
            parallelUrls,
        };
    }
    // If there is a click-tracking URL, and it is not a parallel tracker,
    // that URL will replace the configured Symbol `clickUrl`. Otherwise,
    // we just stick with the `clickUrl` that we started with.
    const trackingOverride = (_a = clickTracking.find(t => !t.isParallel)) === null || _a === void 0 ? void 0 : _a.url;
    return {
        destinationUrl: trackingOverride || symbol.clickUrl,
        parallelUrls,
    };
};
/**
 * Get any external click tag override, if available.
 */
const getExternalClickTag = () => validUrlOrUndefined(getAdContext().ct);
